import { getMissingPermissionReason, NO_FREE_SEAT_REASON } from "@components/table/members/assign-seats/assign-seats-utils";
import { SeatCheckbox } from "@components/table/members/assign-seats/seat-checkbox";
import { useAssignSeat } from "@components/table/members/assign-seats/use-assign-seat";
import { useSeatsAvailable } from "@components/table/members/assign-seats/use-seats-available";
import { UserAvatarWithMenu } from "@components/user-more-info-menu/user-avatar-with-menu";
import { CheckboxGroup, Dialog, Text } from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { Stack } from "@mui-wrapper";
import { formatUserRoleType } from "@utils/data-display";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { getPrettyName } from "@utils/user-utils";
import { getXgSeatBlockReason } from "@utils/xg-seat-utils";
import { useState } from "react";

/** The title of the dialog */
export const XG_SEAT_REQUIRED_TITLE = "Sphere XG seat required";

/** Where the seat is assigned from, as tracked */
const LOCATION = "xg-seat-required-for-role-dialog";

/** The events tracked when the dialog is confirmed or cancelled, e.g. WorkspaceMemberEvents */

/**
 * Dialog shown when a workspace, group or project role needs a Sphere XG seat that the member does not hold. It offers
 * the seat the same way as the Manage seats dialog, and confirming it assigns the seat first and
 * changes the role afterwards, so that the member never takes on the role without the seat.
 */
export function XgSeatRequiredForRoleDialog({
  member,
  role,
  events,
  onClose,
  onSeatAssigned
}) {
  const {
    assignSeat
  } = useAssignSeat();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    canManageXgSeats
  } = useHasUserValidRoleCompanyLevel();
  const freeSeats = useSeatsAvailable().xgSeat;
  const [isSeatChecked, setIsSeatChecked] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  // The free seats when saving started, see seatsAvailable
  const [freeSeatsWhenSaving, setFreeSeatsWhenSaving] = useState(0);

  // The seats in use are fetched again as soon as the seat is assigned, which must neither change
  // the count nor block the seat that is being assigned right now
  const seatsAvailable = isLoading ? freeSeatsWhenSaving : freeSeats;
  const blockReason = isLoading ? undefined : getBlockReason();
  const roleName = formatUserRoleType(role);
  const option = {
    seat: "xgSeat",
    isHeld: false,
    blockReason,
    isOffered: !blockReason
  };
  const isChecked = isSeatChecked && !blockReason;
  return <Dialog title={XG_SEAT_REQUIRED_TITLE} confirmText="Assign seat and change role" open onConfirm={assignSeatAndChangeRole} showSpinner={isLoading}
  // Cancelling cannot stop the seat from being assigned and the role changed afterwards
  disabled={isLoading} isConfirmDisabled={!isChecked} onCancel={cancel} dataTestId="xg-seat-required-for-role-dialog">
      <Stack sx={{
      gap: 2
    }}>
        <Text variant="bodyM">
          Only members with a Sphere XG seat can be <strong>{roleName}</strong>{" "}
          in this workspace.
        </Text>

        <CheckboxGroup label={<Stack direction="row" component="span" sx={{
        alignItems: "center",
        gap: 1
      }}>
              Assign a seat to
              <UserAvatarWithMenu member={member} />
              <strong>
                <var>{getPrettyName(member)}</var>
              </strong>
            </Stack>} checkboxGroupSx={{
        gap: 1,
        mt: 1
      }}>
          <SeatCheckbox option={option} isChecked={isChecked} seatsAvailable={seatsAvailable} onChange={(_, shouldAssign) => setIsSeatChecked(shouldAssign)} />
        </CheckboxGroup>
      </Stack>
    </Dialog>;

  /** Returns why the seat cannot be assigned, or undefined when it can */
  function getBlockReason() {
    if (!canManageXgSeats) {
      return getMissingPermissionReason("xgSeat");
    }
    return getXgSeatBlockReason(member) ?? (seatsAvailable > 0 ? undefined : NO_FREE_SEAT_REASON);
  }

  /** Closes the dialog without assigning the seat, which leaves the role as it is */
  function cancel() {
    trackEvent({
      name: events.cancelXgSeatRequiredForRole,
      props: {
        role
      }
    });
    onClose();
  }

  /**
   * Assigns the seat and changes the role afterwards. The dialog stays open when the seat could not
   * be assigned, so that the user can try again, and the role is left as it is.
   */
  async function assignSeatAndChangeRole() {
    setFreeSeatsWhenSaving(freeSeats);
    setIsLoading(true);
    trackEvent({
      name: events.confirmXgSeatRequiredForRole,
      props: {
        role,
        seatsAvailable
      }
    });
    if (!(await assignSeat(member, "xgSeat", LOCATION))) {
      setIsLoading(false);
      return;
    }
    await onSeatAssigned();
    onClose();
  }
}